/**
 * Shared wiring for the “Unique build ID” block (`components/uniqueBuildCode.isml`) used in
 * the save-build and share-build flyout views: POST `SaveBuild-GenerateUniqueKey` and copy-to-clipboard.
 *
 * Implementation lives under {@link uniqueBuildCode.helpers} so `processInclude` only invokes
 * {@link uniqueBuildCodeView} at boot — other navigation modules match the same pattern.
 */
const VIEWS_WITH_UNIQUE_CODE = ['save-build', 'share-build'];

export const uniqueBuildCode = {
  /**
   * Idempotent; call once from `navigation.initSubViews` (before or after view-specific modules).
   */
  uniqueBuildCodeView() {
    this.helpers.bootstrap();
  },

  /**
   * State + handlers (not invoked by `processInclude`).
   */
  helpers: {
    ambListenerWired: false,

    /**
     * Views that already have a delegated copy listener attached.
     */
    copyWiredViews: new WeakSet(),

    bootstrap() {
      if (this.ambListenerWired) {
        return;
      }

      this.ambListenerWired = true;
      document.body.addEventListener('amb:modal-view-changed', event => this.onModalViewChanged(event));
    },

    /**
     * @param {Event} event
     */
    onModalViewChanged(event) {
      const viewName = event.detail?.view;

      if (!VIEWS_WITH_UNIQUE_CODE.includes(viewName)) {
        return;
      }

      const view = document.querySelector(`.configurator-modal .modal-view[view="${viewName}"]`);

      if (!view) {
        return;
      }

      this.fetchUniqueCode(view);
      this.wireCopyOnce(view);
    },

    /**
     * @param {HTMLElement} view
     */
    wireCopyOnce(view) {
      if (this.copyWiredViews.has(view)) {
        return;
      }

      view.addEventListener('click', event => this.onViewClick(event, view));
      this.copyWiredViews.add(view);
    },

    /**
     * @param {MouseEvent} event
     * @param {HTMLElement} view
     */
    onViewClick(event, view) {
      const btn = event.target.closest('.configuration-code-copy-btn');

      if (!btn || !view.contains(btn)) {
        return;
      }

      event.preventDefault();

      const codeEl = view.querySelector('.configuration-code');
      const code = (codeEl?.textContent || '').trim();

      if (!code) {
        return;
      }

      const toast = view.querySelector('.toast.configuration-code-copy-toast');

      void navigator.clipboard.writeText(code).then(() => {
        if (toast && window.jQuery) {
          $(toast).toast('show');
        }
      });
    },

    /**
     * POST the current variation + URL context to generate (or reuse) the unique build code.
     *
     * @param {HTMLElement} view
     */
    fetchUniqueCode(view) {
      const container = view.querySelector('.configuration-code-container');
      const codeEl = view.querySelector('.configuration-code');
      const modalContent = view.closest('.modal-content');
      const url = container?.dataset.action;

      if (!container || !codeEl || !url) {
        return;
      }

      const productEl = document.querySelector('#current-product');
      const selectedVariationId = productEl?.getAttribute('data-selectedvariationid') || '';

      const urlParams = window.location.search.substring(1);
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code') || '';
      const userId = params.get('userId') || '';
      const generateUniqueID = !(code !== '' && userId !== '');

      // Showroom-stock / CXP flows: mirror the legacy offline share handler so the generated build
      // code reserves (or releases) stock consistently. reserveStock is false when the build is tied
      // to an existing showroom vehicle (showroomStock=true) or the same VIN (sameVin=true).
      const showroomStock = params.get('showroomStock') || '';
      const sameVin = params.get('sameVin') || '';
      const reserveStock = !(showroomStock === 'true' || sameVin === 'true');

      const $modalContent = modalContent ? $(modalContent) : null;
      const $modal = $(view.closest('.configurator-modal'));

      if ($modalContent?.spinner) {
        $modalContent.spinner().start();
        $modal.css('pointer-events', 'none');
      }

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        data: {
          pid: selectedVariationId,
          urlParams,
          generateUniqueID,
          code,
          reserveStock,
          showroomStock
        },
        success: data => {
          if (data?.result?.uniqueCode) {
            codeEl.textContent = data.result.uniqueCode;
          }

          if (typeof data?.modal === 'string') {
            try {
              window.globalAccessoryData = JSON.parse(data.modal);
            } catch (e) {
              // non-JSON modal payload — ignore
            }
          }

          if (data?.uniqueCode !== undefined) {
            window.globalSaveuniqueCode = data.uniqueCode;
          }
        },
        complete: () => {
          if ($modalContent?.spinner) {
            $modalContent.spinner().stop();
            $modal.css('pointer-events', 'auto');
          }
        }
      });
    }
  }
};
